import { type ReactElement } from 'react';
import { useSearchParams } from 'react-router-dom';
import { componentSizes } from '@/theme/componentSizes';
import { Box } from '@/ui/primitives';
import { DataTable } from './DataTable';
import { buildServerDataGridStateParams } from './grid/serverDataGridStateParams';
import { SkeletonDataGrid } from './grid/SkeletonDataGrid';
import { ServerDataTableFrame } from './ServerDataTableFrame';
import { buildServerDataTableGridProps } from './serverDataTableGridProps';
import { resolveServerDataTableStyles } from './serverDataTableStyles';
import { resolveServerDataTableViewState } from './serverDataTableViewState';
import { useServerDataTableGridModel } from './useServerDataTableGridModel';
import type { ServerDataTableProps } from './dataTableShared';
import type { GridValidRowModel } from '@/ui/primitives';
import type { DataGridProps as MuiDataGridProps } from '@mui/x-data-grid';

export const ServerDataTable = <T extends GridValidRowModel>(
  props: ServerDataTableProps<T>,
): ReactElement => {
  const {
    queryPath,
    queryFn,
    initialData,
    columns,
    getRowId,
    pageSizeOptions,
    initialPageSize,
    height,
    noRowsText = 'No results found',
    filteredNoRowsText,
    onRowsChange,
    onQueryMeta,
    onDataUpdatedAt,
    fixedFilters,
    search,
    initialSortModel: initialSortModelProp,
    initialFilterModel: initialFilterModelProp,
    queryKeyPrefix,
    onRefetch,
    onInvalidate,
    onRowClick,
    onCellClick,
    onColumnHeaderClick,
    onQueryInput,
    queryKey: providedQueryKey,
    isAuthenticated = true,
    organisationId,
    projectId,
    persistKey,
    syncStateToUrl = false,
    autoHeight = false,
    disableColumnMenu = true,
    disableColumnSorting = true,
    disableColumnResize = false,
    showColumnVerticalBorder,
    showCellVerticalBorder,
    showToolbar = false,
    showLastUpdated = false,
    getRowHeight,
    skeletonRowCount: skeletonRowCountProp,
    hidePaginationControls = false,
    interactionMode = 'interactive',
    disablePageSizeSelect = false,
    hideFooter = false,
    hideSkeletonFooter = false,
    skeletonCellPaddingY,
    skeletonRowHeight,
    skeletonGetRowHeight,
    showSkeletonOnFetch = false,
    showPaginationSkeletonWhenRowsVisible = false,
    columnVisibilityModel: columnVisibilityModelProp,
    columnVisibilityPresets,
    columnVisibilityPreset,
    refetchIntervalMs,
    staleTimeMs,
    refetchOnWindowFocus,
    refetchOnMount,
    externalLoading = false,
    sx,
    containerSx,
    gridProps,
  } = props;
  const [searchParams] = useSearchParams();
  const { containerStyles, dataGridSx } = resolveServerDataTableStyles({
    containerSx,
    sx,
  });
  const stateParams = buildServerDataGridStateParams<T>({
    queryPath,
    queryFn,
    initialData,
    columns,
    pageSizeOptions,
    initialPageSize,
    height,
    noRowsText,
    filteredNoRowsText,
    onRowsChange,
    onQueryMeta,
    onDataUpdatedAt,
    fixedFilters,
    search,
    initialSortModel: initialSortModelProp,
    initialFilterModel: initialFilterModelProp,
    queryKeyPrefix,
    queryKey: providedQueryKey,
    restoreFilterModel: interactionMode !== 'static',
    onRefetch,
    onInvalidate,
    onQueryInput,
    isAuthenticated,
    organisationId,
    projectId,
    persistKey,
    syncStateToUrl,
    autoHeight,
    columnVisibilityModel: columnVisibilityModelProp,
    columnVisibilityPresets,
    columnVisibilityPreset,
    refetchIntervalMs,
    staleTimeMs,
    refetchOnWindowFocus,
    refetchOnMount,
    skeletonGetRowHeight,
    getRowHeight,
  });

  const {
    errorMessage,
    rowsData,
    rowCount,
    showInitialSkeleton,
    showFetchSkeleton,
    showPaginationSkeleton,
    queryIsFetching,
    queryIsFetched,
    lastUpdatedAt,
    resolvedPageSizeOptions,
    resolvedHeight,
    resolvedNoRowsText,
    resolvedSkeletonRowHeight,
    paginationModel,
    columnVisibilityModel,
    columnsWithWidths,
    captureScrollAnchor,
    filterModel,
    handleFilterModelChange,
    handlePaginationModelChange,
    handleSortModelChange,
    setColumnVisibilityModel,
    handleColumnWidthChange,
    sortModel,
    suppressFocusOutline,
  } = useServerDataTableGridModel<T>({
    stateParams,
    columns,
    interactionMode,
    onColumnHeaderClick,
  });
  const {
    effectiveErrorMessage,
    effectiveRowCount,
    effectiveRowsData,
    gridReadyState,
    showPlaceholderGrid,
    showSkeletonGrid,
    skeletonTotalRowCount,
  } = resolveServerDataTableViewState<T>({
    errorMessage,
    externalLoading,
    forcedLoadStateParam: searchParams.get('loadState'),
    queryIsFetched,
    queryIsFetching,
    rowCount,
    rowsData,
    showFetchSkeleton,
    showInitialSkeleton,
    showPaginationSkeleton,
    showPaginationSkeletonWhenRowsVisible,
    showSkeletonOnFetch,
  });
  const skeletonRowCount = skeletonRowCountProp ?? paginationModel.pageSize;
  const emptyOverlayHeight =
    typeof skeletonRowHeight === 'number'
      ? skeletonRowCount * skeletonRowHeight
      : componentSizes.dataGrid.stateMinHeight;
  const resolvedViewGridProps = buildServerDataTableGridProps<T>({
    autoHeight,
    captureScrollAnchor,
    columnVisibilityModel,
    columns: columnsWithWidths,
    dataGridSx,
    disableColumnMenu,
    disableColumnResize,
    disableColumnSorting,
    disablePageSizeSelect,
    emptyOverlayHeight,
    effectiveErrorMessage,
    effectiveRowCount,
    effectiveRowsData,
    filterModel,
    getRowHeight,
    getRowId,
    gridProps: gridProps as Partial<MuiDataGridProps> | undefined,
    handleColumnWidthChange,
    handleFilterModelChange,
    handlePaginationModelChange,
    handleSortModelChange,
    hideFooter,
    hidePaginationControls,
    onCellClick,
    onColumnHeaderClick,
    onRowClick,
    paginationModel,
    queryIsFetching,
    resolvedNoRowsText,
    resolvedPageSizeOptions,
    setColumnVisibilityModel,
    showCellVerticalBorder,
    showColumnVerticalBorder,
    showToolbar,
    sortModel,
    suppressFocusOutline,
  });
  const skeletonGrid = (
    <SkeletonDataGrid<T>
      columns={columnsWithWidths}
      loadingRowCount={skeletonRowCount}
      totalRowCount={skeletonTotalRowCount}
      pageSizeOptions={resolvedPageSizeOptions}
      pageSize={paginationModel.pageSize}
      paginationModel={paginationModel}
      rowHeight={skeletonRowHeight}
      autoHeight={autoHeight}
      showToolbar={showToolbar}
      getRowId={getRowId}
      getRowHeight={resolvedSkeletonRowHeight}
      cellPaddingY={skeletonCellPaddingY}
      columnVisibilityModel={columnVisibilityModel}
      hideFooter={hideSkeletonFooter || hideFooter}
      sx={dataGridSx}
    />
  );
  const dataTableGrid = <DataTable gridProps={resolvedViewGridProps} />;

  return (
    <ServerDataTableFrame
      containerStyles={containerStyles}
      dataError={effectiveErrorMessage}
      dataState={gridReadyState}
      dataTable={dataTableGrid}
      hasRows={effectiveRowsData.length > 0}
      isFetching={queryIsFetching || externalLoading}
      lastUpdatedAt={lastUpdatedAt}
      placeholder={
        <Box
          aria-hidden="true"
          sx={{
            visibility: 'hidden',
          }}
        >
          {skeletonGrid}
        </Box>
      }
      resolvedHeight={resolvedHeight}
      showLastUpdated={showLastUpdated}
      showPlaceholderGrid={showPlaceholderGrid}
      showSkeletonGrid={showSkeletonGrid}
      skeleton={skeletonGrid}
      skeletonHeightAnchor={showSkeletonGrid && effectiveRowsData.length > 0 ? dataTableGrid : null}
    />
  );
};
